/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  selectors.js
 */

import {
  selectCurrentKey,
  selectError,
  selectFetching,
  selectFetchingKey,
} from '@zcode/nr/es/shared/modules/meta/selectors'
import type { PriceChart, PriceChartDataForRoom, RoomObj, State } from '@zcode/state-type'
import { dollarize } from '@zcode/utils'
import { compose } from 'redux'

import { getRecentPrice } from '../../../components/PriceChart/utils/getRecent'

const selectPriceChart = (state: State) => state?.priceChart

const selectCityData = (
  priceChart: PriceChart | { cityPriceData: Array<PriceChartDataForRoom | undefined> }
) => {
  const priceData = priceChart?.cityPriceData
  return priceData
}

const selectCityMeta = (priceChart: PriceChart) => priceChart?.cityPriceMeta

const selectHoodData = (
  priceChart: PriceChart | { hoodPriceData: Array<PriceChartDataForRoom | undefined> }
) => {
  const hoodData = priceChart?.hoodPriceData
  return hoodData
}

const selectHoodMeta = (priceChart: PriceChart) => priceChart?.hoodPriceMeta

export const selectDetailPriceChart = compose(
  (priceChart) => priceChart?.detailPriceChart,
  selectPriceChart
)

export const selectLocalePriceChart = compose(
  (priceChart) => priceChart?.localePriceChart,
  selectPriceChart
)

export const priceChartDataSelectorsCreator = (
  priceChartSelector: (state: State) => PriceChart
) => {
  const selectCityPriceChartData = compose(selectCityData, priceChartSelector)
  const selectHoodPriceChartData = compose(selectHoodData, priceChartSelector)
  const selectCityBedroomData = (state: State, { room }: RoomObj) => {
    const data = selectCityPriceChartData(state)
    return data?.[room]
  }
  const selectCitySeries = (state: State, { room }: RoomObj) => {
    const bedroomData = selectCityBedroomData(state, { room })
    return bedroomData?.series
  }
  const selectHoodBedroomData = (state: State, { room }: RoomObj) => {
    const data = selectHoodPriceChartData(state)
    return data?.[room]
  }
  const selectHoodSeries = (state: State, { room }: RoomObj) => {
    const bedroomData = selectHoodBedroomData(state, { room })
    return bedroomData?.series
  }
  const selectRecentPrice = (state: State, { room }: RoomObj) => {
    const hoodData = selectHoodPriceChartData(state)
    const citySeries = selectCitySeries(state, { room })
    const cityPrice = getRecentPrice(citySeries)
    const hoodSeries = selectHoodSeries(state, { room })
    const hoodPrice = getRecentPrice(hoodSeries)
    return hoodData?.length ? dollarize(hoodPrice) : dollarize(cityPrice)
  }
  return {
    selectCityPriceChartData,
    selectHoodPriceChartData,
    selectCityBedroomData,
    selectCitySeries,
    selectHoodBedroomData,
    selectHoodSeries,
    selectRecentPrice,
  }
}

export const priceChartMetaSelectorsCreator = (
  priceChartSelector: (state: State) => PriceChart
) => {
  const selectCityPriceChartMeta = compose(selectCityMeta, priceChartSelector)
  const selectHoodPriceChartMeta = compose(selectHoodMeta, priceChartSelector)
  const selectCityPriceChartCurrentKey = compose(selectCurrentKey, selectCityPriceChartMeta)
  const selectCityPriceChartFetchingError = compose(selectError, selectCityPriceChartMeta)
  const selectCityPriceChartFetching = compose(selectFetching, selectCityPriceChartMeta)
  const selectCityPriceChartFetchingKey = compose(selectFetchingKey, selectCityPriceChartMeta)
  const selectHoodPriceChartCurrentKey = compose(selectCurrentKey, selectHoodPriceChartMeta)
  const selectHoodPriceChartFetchingError = compose(selectError, selectHoodPriceChartMeta)
  const selectHoodPriceChartFetching = compose(selectFetching, selectHoodPriceChartMeta)
  const selectHoodPriceChartFetchingKey = compose(selectFetchingKey, selectHoodPriceChartMeta)
  return {
    selectCityPriceChartMeta,
    selectHoodPriceChartMeta,
    selectCityPriceChartCurrentKey,
    selectCityPriceChartFetchingError,
    selectCityPriceChartFetching,
    selectCityPriceChartFetchingKey,
    selectHoodPriceChartCurrentKey,
    selectHoodPriceChartFetchingError,
    selectHoodPriceChartFetching,
    selectHoodPriceChartFetchingKey,
  }
}
